import { Box, Button, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useApiMutation } from "src/hooks/useApiMutation";
import type { ICreatePrizePayload, IRafflePrize } from "src/Types/module/raffleDraw/raffleDraw";

interface Props {
	prize?: IRafflePrize | null;
	handleClose: () => void;
}

const PrizeFormModal: React.FC<Props> = ({ prize, handleClose }) => {
	const isEdit = Boolean(prize?.id);

	const [prizeNo, setPrizeNo] = useState<string>(prize?.prize_no || "");
	const [prizeDetails, setPrizeDetails] = useState<string>(
		prize?.prize_details || ""
	);

	useEffect(() => {
		if (prize) {
			setPrizeNo(prize.prize_no || "");
			setPrizeDetails(prize.prize_details || "");
		} else {
			setPrizeNo("");
			setPrizeDetails("");
		}
	}, [prize]);

	const { mutate: submitPrize, isPending, isSuccess } = useApiMutation<
		ICreatePrizePayload,
		void
	>({
		method: isEdit ? "PATCH" : "POST",
		endPoint: isEdit
			? `/api/raffle-draw/prize/${prize?.id}`
			: "/api/raffle-draw/prize",
		invalidateQueries: ["raffle-prize"],
	});

	useEffect(() => {
		if (isSuccess) {
			handleClose();
		}
	}, [isSuccess]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!prizeNo.trim()) {
			alert("Please provide a prize number (e.g. P-01)");
			return;
		}

		submitPrize({
			prize_no: prizeNo.trim(),
			prize_details: prizeDetails.trim(),
		});
	};

	return (
		<Box
			component="form"
			onSubmit={handleSubmit}
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 2,
				pt: 1,
				pb: 0.5,
			}}
		>
			<TextField
				fullWidth
				required
				size="small"
				label="Prize Number"
				placeholder="e.g. P-01"
				value={prizeNo}
				onChange={(e) => setPrizeNo(e.target.value)}
				InputLabelProps={{ shrink: true }}
			/>

			<TextField
				fullWidth
				multiline
				rows={3}
				size="small"
				label="Prize Details"
				placeholder="Enter prize description or item name..."
				value={prizeDetails}
				onChange={(e) => setPrizeDetails(e.target.value)}
				InputLabelProps={{ shrink: true }}
			/>

			<Box
				sx={{
					display: "flex",
					justifyContent: "flex-end",
					gap: 1.5,
					mt: 1,
				}}
			>
				<Button
					variant="outlined"
					color="inherit"
					onClick={handleClose}
					disabled={isPending}
				>
					<span>Cancel</span>
				</Button>
				<Button
					type="submit"
					variant="contained"
					color="primary"
					disabled={isPending}
					sx={{ minWidth: 120, fontWeight: 600, textTransform: "none" }}
				>
					<span>
						{isPending
							? "Saving..."
							: isEdit
								? "Update Prize"
								: "Create Prize"}
					</span>
				</Button>
			</Box>
		</Box>
	);
};

export default PrizeFormModal;
